/**
 * HiringPipeline — Kanban ↔ List view. No requisitions.
 * Convert-to-Hire lets HR pick clinic + role at conversion time.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { fmtDate, fmtDateTime, fmtMonYY } from '../../lib/date';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import InterviewToolkit from './InterviewToolkit';
import { Plus, LayoutGrid, List as ListIcon, Upload, UserCheck,
         FileText, Trash2, Download, HelpCircle, ChevronDown, ChevronUp, Bookmark, Pencil, Sparkles, AlertTriangle, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A', ok: '#3B6F5D',
};

// AI screener recommendation → badge styling.
const SCREEN_STYLES = {
  strong:   { bg: '#E4F1EA', fg: '#2F5F4A', label: 'Strong fit' },
  possible: { bg: '#FBF3E0', fg: '#7A5E22', label: 'Possible fit' },
  weak:     { bg: '#F6E4E4', fg: '#8A3A3A', label: 'Weak fit' },
};

function AIScreenBadge({ screen, compact = false }) {
  if (!screen) return null;
  if (screen.status === 'unscreenable') {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1"
            style={{ background: '#F4F1F8', color: C.soft }}
            data-testid="hr-ai-badge-unscreenable"
            title={screen.reason || 'AI could not read this resume'}>
        <Sparkles size={9} /> Unscreenable
      </span>
    );
  }
  const rec = screen.recommendation;
  const s = SCREEN_STYLES[rec] || SCREEN_STYLES.possible;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1"
          style={{ background: s.bg, color: s.fg }}
          data-testid={`hr-ai-badge-${rec}`}
          title={screen.summary || ''}>
      <Sparkles size={9} /> {s.label} · {screen.fit_score}
    </span>
  );
}

const CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial',
                  'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];

const STAGES = [
  'New Application', 'Resume Review',
  'Phone Screen Scheduled', 'Phone Screen Completed',
  'Interview Scheduled', 'Interview Completed',
  'Clinic Visit Scheduled', 'Clinic Visit Completed',
  'Reference Check', 'Offer Extended', 'Offer Accepted',
  'Hired', 'Offer Declined', 'Withdrawn', 'Not Moved Forward',
  'Talent Pool',
];
const ACTIVE_STAGES = STAGES.slice(0, 11);
const CLOSED_STAGES = ['Hired', 'Offer Declined', 'Withdrawn', 'Not Moved Forward', 'Talent Pool'];
const HIRE_ROLES = ['SLP', 'CF-SLP', 'SLPA', 'PRN', 'Student-Extern',
                     'Office Director/SLP', 'Admin'];
const ENTITIES = ['Thrive', 'Ludo'];
const EMPLOYMENT_TYPES = ['FT', 'PT', 'PRN'];
const EMPLOYEE_TYPES = ['Employee', 'Contractor'];

function StageBadge({ stage }) {
  const closed = CLOSED_STAGES.includes(stage);
  return (
    <span className="text-[10px] px-2 py-0.5 rounded font-semibold"
          style={{ background: closed ? '#F4F1F8' : '#FBF3E0', color: closed ? C.plum : C.gold }}>
      {stage}
    </span>
  );
}

function CandidateCard({ c, onOpen }) {
  const sd = c.scheduled_dates || {};
  const next = sd.phone_screen || sd.interview || sd.clinic_visit;
  const nextLabel = sd.phone_screen ? 'Phone' : sd.interview ? 'Interview' : sd.clinic_visit ? 'Clinic visit' : '';
  const viaIndeed = (c.source || '').toLowerCase() === 'indeed';
  return (
    <div className="p-3 rounded-lg border cursor-pointer hover:shadow-sm bg-white"
         style={{ borderColor: C.border }} onClick={() => onOpen(c)}
         data-testid={`hr-cand-card-${c.candidate_id}`}>
      <div className="flex items-start gap-1.5">
        <div className="text-sm font-semibold truncate flex-1" style={{ color: C.ink }}>{c.name}</div>
        {viaIndeed && (
          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                style={{ background: '#0D6EFD', color: '#fff' }}
                data-testid={`hr-cand-card-indeed-${c.candidate_id}`}>
            Indeed
          </span>
        )}
      </div>
      <div className="text-[11px] mt-0.5" style={{ color: C.soft }}>
        {c.applied_role} · {(c.preferred_clinics || []).join(', ') || '—'}
      </div>
      <div className="text-[10px] mt-1" style={{ color: C.soft }}>
        {c.source} · {fmtDate(c.application_date)}
      </div>
      {c.ai_screen && (
        <div className="mt-1.5" data-testid={`hr-cand-card-ai-${c.candidate_id}`}>
          <AIScreenBadge screen={c.ai_screen} compact />
          {(c.ai_screen.red_flags || []).length > 0 && (
            <span className="ml-1 text-[10px] inline-flex items-center gap-0.5"
                  style={{ color: C.rose }} title={c.ai_screen.red_flags.join('; ')}>
              <AlertTriangle size={9} /> {c.ai_screen.red_flags.length} flag{c.ai_screen.red_flags.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      {next && (
        <div className="mt-1.5 text-[10px] px-1.5 py-0.5 rounded inline-block"
             style={{ background: '#F4F1F8', color: C.plum }}
             data-testid={`hr-cand-card-next-${c.candidate_id}`}>
          {nextLabel}: {String(next).replace('T', ' ').slice(0, 16)}
        </div>
      )}
      {c.notes && (
        <div className="mt-1.5 text-[11px] p-1.5 rounded"
             style={{ background: '#FBF8F4', color: C.ink2 }}
             data-testid={`hr-cand-card-notes-${c.candidate_id}`}>
          {c.notes.length > 90 ? `${c.notes.slice(0, 90)}…` : c.notes}
        </div>
      )}
    </div>
  );
}

function AIScreenCard({ candidate, onChanged }) {
  const [rescreening, setRescreening] = useState(false);
  const screen = candidate.ai_screen;
  const hasResume = !!candidate.resume;
  const rescreen = async () => {
    if (!hasResume) return toast.error('Upload a resume before screening');
    setRescreening(true);
    try {
      await api.post(`/hr/candidates/${candidate.candidate_id}/screen`);
      toast.success('AI screen complete');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Screen failed');
    } finally { setRescreening(false); }
  };
  return (
    <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-ai-screen">
      <div className="flex items-center gap-2">
        <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>
          <Sparkles size={11} className="inline -mt-0.5 mr-1" /> AI Pre-Screener
        </Label>
        <Button size="sm" variant="outline" className="ml-auto h-7"
                onClick={rescreen} disabled={rescreening || !hasResume}
                data-testid="hr-cand-ai-rescreen">
          {rescreening ? 'Screening…' : (screen ? 'Re-screen' : 'Screen resume')}
        </Button>
      </div>
      {!screen && !rescreening && (
        <div className="mt-2 text-[11px]" style={{ color: C.soft }}>
          {hasResume ? 'Click Screen resume to get an AI fit score.'
                     : 'No resume on file yet.'}
        </div>
      )}
      {screen?.status === 'unscreenable' && (
        <div className="mt-2 text-[11px]" style={{ color: C.rose }}>
          Unscreenable — {screen.reason}
        </div>
      )}
      {screen?.status === 'screened' && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <AIScreenBadge screen={screen} />
            <span className="text-[10px]" style={{ color: C.soft }}>
              Role: {screen.role_match} · {screen.years_experience || 0} yrs
            </span>
          </div>
          {screen.summary && (
            <div className="text-[11px] p-2 rounded" style={{ background: C.paper, color: C.ink2 }}>
              {screen.summary}
            </div>
          )}
          {(screen.highlights || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: C.teal }}>Highlights</div>
              <ul className="mt-1 text-[11px] space-y-0.5" style={{ color: C.ink2 }}>
                {screen.highlights.map((h, i) => <li key={i}>• {h}</li>)}
              </ul>
            </div>
          )}
          {(screen.red_flags || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: C.rose }}>
                <AlertTriangle size={10} className="inline -mt-0.5 mr-0.5" /> Red flags
              </div>
              <ul className="mt-1 text-[11px] space-y-0.5" style={{ color: C.rose }}>
                {screen.red_flags.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
          )}
          {(screen.credentials_found || []).length > 0 && (
            <div className="text-[10px]" style={{ color: C.soft }}>
              Credentials: {screen.credentials_found.join(', ')}
            </div>
          )}
          <div className="text-[9px]" style={{ color: C.soft }}>
            Screened by {screen.model} · {String(screen.ran_at || '').slice(0, 16).replace('T', ' ')}
          </div>
        </div>
      )}
    </Card>
  );
}

function NewCandidateDialog({ open, onClose, onCreated }) {
  const emptyForm = { name: '', email: '', phone: '', applied_role: 'SLP',
    preferred_clinics: [], source: 'Direct', entity: 'Thrive',
    employment_preference: 'FT', employee_type: 'Employee', notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [resumeFile, setResumeFile] = useState(null);
  const submit = async (stageOverride) => {
    if (!form.name.trim()) return toast.error('Name required');
    const payload = { ...form };
    if (stageOverride) payload.stage = stageOverride;
    try {
      const { data } = await api.post('/hr/candidates', payload);
      if (resumeFile && data?.candidate_id) {
        const fd = new FormData();
        fd.append('file', resumeFile);
        try {
          await api.post(`/hr/candidates/${data.candidate_id}/resume`, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch { toast.error('Candidate created but resume upload failed'); }
      }
      toast.success(stageOverride === 'Talent Pool'
        ? 'Saved to Talent Pool for future openings'
        : 'Candidate created');
      onCreated();
      onClose();
      setResumeFile(null);
      setForm(emptyForm);
    } catch { toast.error('Create failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="hr-cand-new-dialog">
        <DialogHeader><DialogTitle>New candidate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="hr-cand-new-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Role</Label>
              <Select value={form.applied_role} onValueChange={v => setForm({ ...form, applied_role: v })}>
                <SelectTrigger data-testid="hr-cand-new-role"><SelectValue /></SelectTrigger>
                <SelectContent>{HIRE_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Entity</Label>
              <Select value={form.entity} onValueChange={v => setForm({ ...form, entity: v })}>
                <SelectTrigger data-testid="hr-cand-new-entity"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Employment</Label>
              <Select value={form.employment_preference} onValueChange={v => setForm({ ...form, employment_preference: v })}>
                <SelectTrigger data-testid="hr-cand-new-employment"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Employee type</Label>
              <Select value={form.employee_type} onValueChange={v => setForm({ ...form, employee_type: v })}>
                <SelectTrigger data-testid="hr-cand-new-emp-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Preferred clinics (multi-select)</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="hr-cand-new-clinics">
              {CLINICS.map(cl => (
                <button key={cl} onClick={() => setForm({ ...form, preferred_clinics:
                  form.preferred_clinics.includes(cl)
                    ? form.preferred_clinics.filter(x => x !== cl)
                    : [...form.preferred_clinics, cl] })}
                    className={`text-[10px] px-2 py-1 rounded border ${form.preferred_clinics.includes(cl) ? 'bg-[#4A3B5F] text-white border-transparent' : 'bg-white'}`}
                    style={{ borderColor: C.border }}>{cl}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Resume (optional · PDF, DOC, DOCX, TXT · max 10 MB)</Label>
            <Input type="file" accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={e => setResumeFile(e.target.files?.[0] || null)}
              data-testid="hr-cand-new-resume" />
            {resumeFile && <div className="text-[11px] mt-1" style={{ color: C.soft }}>Selected: {resumeFile.name}</div>}
          </div>
          <div>
            <Label className="text-[11px]">Notes (optional)</Label>
            <Textarea rows={3}
              placeholder="Anything HR should remember — interview impressions, availability, referral context, etc."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              data-testid="hr-cand-new-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => submit('Talent Pool')} data-testid="hr-cand-new-save-talent-pool">
            <Bookmark size={14} className="mr-1" /> Save to Talent Pool
          </Button>
          <Button onClick={() => submit()} data-testid="hr-cand-new-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResumeSection({ candidate, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const resume = candidate.resume;
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/hr/candidates/${candidate.candidate_id}/resume`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Resume uploaded');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  const doDownload = async () => {
    try {
      const res = await api.get(`/hr/candidates/${candidate.candidate_id}/resume`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = resume?.file_name || 'resume.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  const doDelete = async () => {
    if (!window.confirm('Delete this resume?')) return;
    try {
      await api.delete(`/hr/candidates/${candidate.candidate_id}/resume`);
      toast.success('Resume removed');
      onChanged();
    } catch { toast.error('Delete failed'); }
  };
  return (
    <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-resume">
      <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Resume</Label>
      {resume ? (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <FileText size={16} style={{ color: C.plum }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" style={{ color: C.ink }}>{resume.file_name}</div>
            <div className="text-[10px]" style={{ color: C.soft }}>
              {Math.round((resume.size_bytes || 0) / 1024)} KB · uploaded {fmtDate(resume.uploaded_at)}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={doDownload} data-testid="hr-cand-resume-download">
            <Download size={14} className="mr-1" /> Download
          </Button>
          <Button size="sm" variant="outline" onClick={doDelete} data-testid="hr-cand-resume-delete">
            <Trash2 size={14} />
          </Button>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <Input type="file"
            accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            onChange={e => doUpload(e.target.files?.[0])}
            disabled={uploading}
            data-testid="hr-cand-resume-upload" />
        </div>
      )}
    </Card>
  );
}

function ConvertDialog({ candidate, open, onClose, onDone }) {
  const [form, setForm] = useState({
    confirmed_clinic: '',
    confirmed_role: candidate?.applied_role === 'CF' ? 'CF-SLP' : (candidate?.applied_role || 'SLP'),
    employment_type: 'FT',
    start_date: '',
    supervisor: '',
  });
  useEffect(() => {
    if (candidate) setForm(f => ({ ...f,
      confirmed_role: candidate.applied_role === 'CF' ? 'CF-SLP' : (candidate.applied_role || 'SLP'),
      confirmed_clinic: (candidate.preferred_clinics || [])[0] || '',
    }));
  }, [candidate]);
  const supervisorRequired = ['CF-SLP', 'SLPA'].includes(form.confirmed_role);
  const submit = async () => {
    if (!form.confirmed_clinic || !form.start_date) return toast.error('Clinic and start date required');
    if (supervisorRequired && !form.supervisor.trim()) return toast.error('Supervisor required for CF-SLP / SLPA');
    try {
      const { data } = await api.post(`/hr/candidates/${candidate.candidate_id}/convert`, form);
      toast.success(`Roster ${data.path} · ${candidate.name} hired`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="hr-convert-dialog">
        <DialogHeader><DialogTitle>Convert to Hire · {candidate?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">Confirmed clinic</Label>
            <Select value={form.confirmed_clinic} onValueChange={v => setForm({ ...form, confirmed_clinic: v })}>
              <SelectTrigger data-testid="hr-convert-clinic"><SelectValue placeholder="Pick a clinic" /></SelectTrigger>
              <SelectContent>{CLINICS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Confirmed role</Label>
            <Select value={form.confirmed_role} onValueChange={v => setForm({ ...form, confirmed_role: v })}>
              <SelectTrigger data-testid="hr-convert-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HIRE_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Employment type</Label>
              <Select value={form.employment_type} onValueChange={v => setForm({ ...form, employment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['FT','PT','PRN'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Start date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="hr-convert-start-date" />
            </div>
          </div>
          {supervisorRequired && (
            <div>
              <Label className="text-[11px]">Supervisor (required for {form.confirmed_role})</Label>
              <Input placeholder="Supervisor name" value={form.supervisor} onChange={e => setForm({ ...form, supervisor: e.target.value })} data-testid="hr-convert-supervisor" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="hr-convert-submit">Convert to Hire</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateDrawer({ candidate, onClose, onChanged }) {
  const [toolkitOpen, setToolkitOpen] = useState(false);
  const [stage, setStage] = useState(candidate?.stage || '');
  const [scheduledDate, setScheduledDate] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [nextAction, setNextAction] = useState(candidate?.next_action || '');
  const [nextDue, setNextDue] = useState(candidate?.next_action_due || '');
  const [notes, setNotes] = useState(candidate?.notes || '');
  const [convertOpen, setConvertOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [details, setDetails] = useState({});
  const [dates, setDates] = useState({});
  useEffect(() => {
    setStage(candidate?.stage || '');
    setNextAction(candidate?.next_action || '');
    setNextDue(candidate?.next_action_due || '');
    setNotes(candidate?.notes || '');
    setDeclineReason('');
    setScheduledDate('');
    setEditOpen(false);
    setDates(candidate?.scheduled_dates || {});
    setDetails({
      name: candidate?.name || '',
      email: candidate?.email || '',
      phone: candidate?.phone || '',
      city: candidate?.city || '',
      applied_role: candidate?.applied_role || 'SLP',
      entity: candidate?.entity || 'Thrive',
      employment_preference: candidate?.employment_preference || 'FT',
      employee_type: candidate?.employee_type || 'Employee',
      source: candidate?.source || 'Direct',
      preferred_clinics: candidate?.preferred_clinics || [],
    });
  }, [candidate?.candidate_id]);
  if (!candidate) return null;
  const isClosedNeg = ['Offer Declined', 'Withdrawn', 'Not Moved Forward'].includes(stage);
  const SCHEDULED_STAGE_TO_SLOT = {
    'Phone Screen Scheduled': 'phone_screen',
    'Interview Scheduled':    'interview',
    'Clinic Visit Scheduled': 'clinic_visit',
  };
  const isScheduledStage = !!SCHEDULED_STAGE_TO_SLOT[stage];
  const saveStage = async () => {
    try {
      const body = { stage, decline_reason: declineReason };
      if (isScheduledStage && scheduledDate) body.scheduled_date = scheduledDate;
      await api.post(`/hr/candidates/${candidate.candidate_id}/stage`, body);
      toast.success('Stage updated');
      onChanged();
    } catch { toast.error('Stage change failed'); }
  };
  const moveToTalentPool = async () => {
    try {
      await api.post(`/hr/candidates/${candidate.candidate_id}/stage`,
        { stage: 'Talent Pool' });
      toast.success('Saved to Talent Pool');
      onChanged();
    } catch { toast.error('Move failed'); }
  };
  const saveAction = async () => {
    try {
      await api.patch(`/hr/candidates/${candidate.candidate_id}`,
        { next_action: nextAction, next_action_due: nextDue || null });
      toast.success('Next action saved');
      onChanged();
    } catch { toast.error('Save failed'); }
  };
  const saveNotes = async () => {
    try {
      await api.patch(`/hr/candidates/${candidate.candidate_id}`, { notes });
      toast.success('Notes saved');
      onChanged();
    } catch { toast.error('Save failed'); }
  };
  const saveDetails = async () => {
    if (!(details.name || '').trim()) return toast.error('Name is required');
    try {
      await api.patch(`/hr/candidates/${candidate.candidate_id}`, details);
      toast.success('Details saved');
      setEditOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const deleteCandidate = async () => {
    if (!window.confirm(`Permanently delete ${candidate.name} and all their history? This cannot be undone.`)) return;
    try {
      await api.delete(`/hr/candidates/${candidate.candidate_id}`);
      toast.success('Candidate deleted');
      onChanged();
      onClose();
    } catch { toast.error('Delete failed'); }
  };
  const saveDates = async () => {
    try {
      await api.patch(`/hr/candidates/${candidate.candidate_id}`,
        { scheduled_dates: dates });
      toast.success('Scheduled dates saved');
      onChanged();
    } catch { toast.error('Save failed'); }
  };
  return (
    <Sheet open={!!candidate} onOpenChange={o => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="hr-cand-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="flex-1">{candidate.name}</span>
            <Button size="sm" variant="outline" onClick={() => setToolkitOpen(true)}
                    data-testid="hr-cand-toolkit-open"
                    className="gap-1">
              <Wrench size={12} /> Interview toolkit
            </Button>
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-details-card">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Candidate details</Label>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditOpen(v => !v)}
                        data-testid="hr-cand-edit-toggle">
                  <Pencil size={13} className="mr-1" /> {editOpen ? 'Cancel' : 'Edit'}
                </Button>
                <Button size="sm" variant="outline" onClick={deleteCandidate}
                        data-testid="hr-cand-delete"
                        style={{ borderColor: '#D9B0B0', color: '#8A3A3A' }}>
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
            {!editOpen ? (
              <>
                <div className="mt-2 text-[11px]" style={{ color: C.soft }}>
                  {candidate.email || '—'} · {candidate.phone || '—'} · {candidate.city || '—'}
                </div>
                <div className="text-[11px]" style={{ color: C.ink2 }}>
                  Applied {candidate.applied_role} · {(candidate.preferred_clinics || []).join(', ') || '—'} · from {candidate.source} · {candidate.entity} · {candidate.employment_preference} · {candidate.employee_type}
                </div>
              </>
            ) : (
              <div className="mt-2 space-y-2">
                <Input placeholder="Full name" value={details.name}
                       onChange={e => setDetails({ ...details, name: e.target.value })}
                       data-testid="hr-cand-edit-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Email" value={details.email}
                         onChange={e => setDetails({ ...details, email: e.target.value })}
                         data-testid="hr-cand-edit-email" />
                  <Input placeholder="Phone" value={details.phone}
                         onChange={e => setDetails({ ...details, phone: e.target.value })}
                         data-testid="hr-cand-edit-phone" />
                </div>
                <Input placeholder="City" value={details.city}
                       onChange={e => setDetails({ ...details, city: e.target.value })}
                       data-testid="hr-cand-edit-city" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]" style={{ color: C.soft }}>Role</Label>
                    <Select value={details.applied_role}
                            onValueChange={v => setDetails({ ...details, applied_role: v })}>
                      <SelectTrigger data-testid="hr-cand-edit-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{HIRE_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px]" style={{ color: C.soft }}>Entity</Label>
                    <Select value={details.entity}
                            onValueChange={v => setDetails({ ...details, entity: v })}>
                      <SelectTrigger data-testid="hr-cand-edit-entity"><SelectValue /></SelectTrigger>
                      <SelectContent>{ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]" style={{ color: C.soft }}>Employment</Label>
                    <Select value={details.employment_preference}
                            onValueChange={v => setDetails({ ...details, employment_preference: v })}>
                      <SelectTrigger data-testid="hr-cand-edit-employment"><SelectValue /></SelectTrigger>
                      <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px]" style={{ color: C.soft }}>Employee type</Label>
                    <Select value={details.employee_type}
                            onValueChange={v => setDetails({ ...details, employee_type: v })}>
                      <SelectTrigger data-testid="hr-cand-edit-emp-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{EMPLOYEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]" style={{ color: C.soft }}>Source</Label>
                  <Input value={details.source}
                         onChange={e => setDetails({ ...details, source: e.target.value })}
                         data-testid="hr-cand-edit-source" />
                </div>
                <div>
                  <Label className="text-[10px]" style={{ color: C.soft }}>Preferred clinics</Label>
                  <div className="flex flex-wrap gap-1 mt-1" data-testid="hr-cand-edit-clinics">
                    {CLINICS.map(cl => (
                      <button key={cl}
                        type="button"
                        onClick={() => setDetails({ ...details, preferred_clinics:
                          (details.preferred_clinics || []).includes(cl)
                            ? (details.preferred_clinics || []).filter(x => x !== cl)
                            : [...(details.preferred_clinics || []), cl] })}
                        className={`text-[10px] px-2 py-1 rounded border ${(details.preferred_clinics || []).includes(cl) ? 'bg-[#4A3B5F] text-white border-transparent' : 'bg-white'}`}
                        style={{ borderColor: C.border }}>{cl}</button>
                    ))}
                  </div>
                </div>
                <Button size="sm" onClick={saveDetails} data-testid="hr-cand-edit-save">
                  Save details
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="mt-1" data-testid="hr-cand-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {isClosedNeg && (
              <Input className="mt-2" placeholder="Decline reason" value={declineReason} onChange={e => setDeclineReason(e.target.value)} data-testid="hr-cand-decline-reason" />
            )}
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" onClick={saveStage} data-testid="hr-cand-stage-save">Save stage</Button>
              {stage === 'Offer Accepted' && (
                <Button size="sm" variant="default" onClick={() => setConvertOpen(true)} data-testid="hr-cand-convert">
                  <UserCheck size={14} className="mr-1" /> Convert to Hire
                </Button>
              )}
              {stage !== 'Talent Pool' && (
                <Button size="sm" variant="outline" onClick={moveToTalentPool} data-testid="hr-cand-save-talent-pool">
                  <Bookmark size={14} className="mr-1" /> Save for future
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-notes-card">
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Notes</Label>
            <Textarea rows={4}
              placeholder="Interview impressions, availability, referral context…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="mt-1"
              data-testid="hr-cand-notes-textarea" />
            <Button size="sm" className="mt-2" onClick={saveNotes} data-testid="hr-cand-notes-save">
              Save notes
            </Button>
          </Card>

          <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-scheduled-card">
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>
              Scheduled interactions
            </Label>
            <div className="text-[10px] mb-2" style={{ color: C.soft }}>
              Edit any date if a screening, interview, or clinic visit gets rescheduled.
            </div>
            {[
              { slot: 'phone_screen', label: 'Phone screen' },
              { slot: 'interview',    label: 'Interview' },
              { slot: 'clinic_visit', label: 'Clinic visit' },
            ].map(({ slot, label }) => (
              <div key={slot} className="flex items-center gap-2 mt-1.5">
                <div className="w-24 text-[11px]" style={{ color: C.ink2 }}>{label}</div>
                <Input
                  type="datetime-local"
                  value={dates[slot] || ''}
                  onChange={e => setDates({ ...dates, [slot]: e.target.value })}
                  className="h-8 flex-1"
                  data-testid={`hr-cand-scheduled-${slot}`}
                />
                {dates[slot] && (
                  <button type="button"
                          onClick={() => setDates({ ...dates, [slot]: '' })}
                          className="text-[11px]" style={{ color: C.rose }}
                          data-testid={`hr-cand-scheduled-${slot}-clear`}>clear</button>
                )}
              </div>
            ))}
            <Button size="sm" className="mt-3" onClick={saveDates}
                    data-testid="hr-cand-scheduled-save">
              Save schedule
            </Button>
          </Card>

          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Next action</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input placeholder="e.g. Schedule phone screen" value={nextAction} onChange={e => setNextAction(e.target.value)} data-testid="hr-cand-next-action" />
              <Input type="date" value={nextDue || ''} onChange={e => setNextDue(e.target.value)} data-testid="hr-cand-next-due" />
            </div>
            <Button size="sm" className="mt-2" onClick={saveAction} data-testid="hr-cand-next-save">Save</Button>
          </Card>

          <ResumeSection candidate={candidate} onChanged={onChanged} />

          <AIScreenCard candidate={candidate} onChanged={onChanged} />

          <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-cand-activity">
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.plum }}>Activity log</Label>
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {(candidate.activity_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="text-[11px] py-1 border-t" style={{ borderColor: C.border, color: C.ink2 }}>
                  <div><span className="font-semibold">{a.type}</span> · {a.user_email}
                    {a.from_stage && <> · {a.from_stage} → {a.to_stage}</>}
                  </div>
                  <div className="text-[10px]" style={{ color: C.soft }}>{a.ts?.slice(0, 16).replace('T', ' ')}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </SheetContent>
      <ConvertDialog candidate={candidate} open={convertOpen}
                     onClose={() => setConvertOpen(false)}
                     onDone={() => { onChanged(); onClose(); }} />
      <InterviewToolkit candidate={candidate}
                        open={toolkitOpen}
                        onOpenChange={setToolkitOpen}
                        onChanged={onChanged} />
    </Sheet>
  );
}

function ImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const downloadTemplate = async () => {
    try {
      const r = await api.get('/hr/candidates/template.xlsx', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'thrive-hr-candidates-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  };
  const submit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/hr/candidates/import-csv', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`Imported ${data.created} · ${data.duplicates.length} duplicates flagged`);
      onImported();
    } catch { toast.error('Import failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="hr-import-dialog">
        <DialogHeader><DialogTitle>Import candidates</DialogTitle></DialogHeader>
        <div className="text-[11px]" style={{ color: C.soft }}>
          Columns: <code>name, email, phone, role, clinic (semicolon-separated for multi), entity, employment, employee_type, source, stage, date</code>. Duplicates detected on email + phone.
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="hr-import-template">
            Download XLSX template
          </Button>
        </div>
        <Input type="file" accept=".csv,.xlsx" onChange={e => setFile(e.target.files?.[0])} data-testid="hr-import-file" />
        {result && (
          <div className="text-[11px] p-2 rounded" style={{ background: C.paper, color: C.ink2 }}>
            Created {result.created}, duplicates {result.duplicates.length}
            {result.duplicates.slice(0, 5).map((d, i) => <div key={i}>· {d.name} ({d.email})</div>)}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={submit} data-testid="hr-import-submit">Import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HiringPipeline() {
  const [view, setView] = useState('kanban');
  const [candidates, setCandidates] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [clinicStaff, setClinicStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ clinic: '', role: '', source: '', entity: '' });
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Collapsible "How to use" helper — persisted in localStorage so HR
  // can hide it after the first read.
  const [helpOpen, setHelpOpen] = useState(() => {
    try { return localStorage.getItem('hr:pipeline-help') !== 'closed'; }
    catch { return true; }
  });
  const toggleHelp = () => {
    setHelpOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem('hr:pipeline-help', next ? 'open' : 'closed'); } catch {}
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      // Same filter goes to both endpoints so the KPI tiles react to
      // the toolbar Clinic / Role / Entity dropdowns.
      const [{ data: cd }, { data: md }] = await Promise.all([
        api.get('/hr/candidates', { params: filter }),
        api.get('/hr/pipeline/metrics', { params: filter }),
      ]);
      setCandidates(cd.candidates || []);
      setMetrics(md);
      // Load currently-staffed roster when the toolbar is scoped to a
      // single clinic. Otherwise clear it — a mixed roster wouldn't
      // help.
      if (filter.clinic) {
        try {
          const { data: st } = await api.get(`/hr/staffing/${encodeURIComponent(filter.clinic)}`);
          setClinicStaff(st);
        } catch { setClinicStaff(null); }
      } else {
        setClinicStaff(null);
      }
    } catch { toast.error('Could not load pipeline'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.clinic, filter.role, filter.source, filter.entity]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map(s => [s, []]));
    for (const c of candidates) (map[c.stage] || map['New Application']).push(c);
    return map;
  }, [candidates]);

  return (
    <div className="space-y-4" data-testid="hr-hiring-pipeline">
      {/* "How to use" collapsible helper — hideable, localStorage-persisted. */}
      <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-pipeline-help">
        <button
          onClick={toggleHelp}
          className="w-full px-4 py-2.5 flex items-center gap-2 text-left"
          style={{ background: C.paper, color: C.ink }}
          data-testid="hr-pipeline-help-toggle">
          <HelpCircle size={14} style={{ color: C.gold }} />
          <span className="text-[11px] uppercase tracking-widest font-bold">
            How to use Hiring Pipeline
          </span>
          <span className="ml-auto">
            {helpOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>
        {helpOpen && (
          <div className="px-4 py-3 text-[12px] leading-relaxed space-y-2" style={{ color: C.ink2 }}>
            <div>
              <span className="font-semibold" style={{ color: C.ink }}>1. Add candidates</span> — click
              <span className="mx-1 px-1.5 py-0.5 rounded border font-medium" style={{ borderColor: C.border, color: C.ink }}>New candidate</span>
              for one, or <span className="px-1.5 py-0.5 rounded border font-medium" style={{ borderColor: C.border, color: C.ink }}>Import CSV</span>
              (download the template first). Duplicates are auto-flagged on email + phone.
            </div>
            <div>
              <span className="font-semibold" style={{ color: C.ink }}>2. Move through stages</span> — drag a card, or open it and change the Stage dropdown. Add a
              <span className="mx-1" style={{ color: C.plum }}>Next action</span> with a due date; overdue items surface on
              <span className="mx-1" style={{ color: C.plum }}>CEO Staffing Overview → Attention</span>.
            </div>
            <div>
              <span className="font-semibold" style={{ color: C.ink }}>3. Convert to Hire</span> — when the stage is
              <span className="mx-1 px-1.5 py-0.5 rounded font-medium" style={{ background: '#FBF3E0', color: C.gold }}>Offer Accepted</span>,
              a button appears in the drawer. Pick the confirmed clinic + role + start date — the roster and 30/60/90-day check-ins are created for you.
            </div>
            <div>
              <span className="font-semibold" style={{ color: C.ink }}>4. Filters</span> — narrow by clinic, role, or entity. Switch between
              <span className="mx-1 inline-flex items-center gap-1"><LayoutGrid size={12} /> Kanban</span> and
              <span className="mx-1 inline-flex items-center gap-1"><ListIcon size={12} /> List</span> using the toggle on the right.
            </div>
            <div className="text-[11px] pt-1" style={{ color: C.soft }}>
              Tip — resumes can be attached at creation or later from a candidate's drawer (PDF / DOC / DOCX / TXT · 10 MB max).
            </div>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filter.entity || 'all'} onValueChange={v => setFilter({ ...filter, entity: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-32" data-testid="hr-filter-entity"><SelectValue placeholder="Both entities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Both entities</SelectItem>
            {ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.clinic || 'all'} onValueChange={v => setFilter({ ...filter, clinic: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40" data-testid="hr-filter-clinic"><SelectValue placeholder="All clinics" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clinics</SelectItem>
            {CLINICS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.role || 'all'} onValueChange={v => setFilter({ ...filter, role: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-32" data-testid="hr-filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {HIRE_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="hr-import-trigger">
            <Upload size={14} className="mr-1" /> Import CSV
          </Button>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid="hr-new-cand-trigger">
            <Plus size={14} className="mr-1" /> New candidate
          </Button>
          <div className="flex border rounded" style={{ borderColor: C.border }}>
            <button className={`px-2 py-1 ${view === 'kanban' ? 'bg-[#4A3B5F] text-white' : ''}`} onClick={() => setView('kanban')} data-testid="hr-view-kanban"><LayoutGrid size={14} /></button>
            <button className={`px-2 py-1 ${view === 'list' ? 'bg-[#4A3B5F] text-white' : ''}`} onClick={() => setView('list')} data-testid="hr-view-list"><ListIcon size={14} /></button>
          </div>
        </div>
      </div>

      {metrics && (
        <div className="grid md:grid-cols-4 gap-3" data-testid="hr-pipeline-metrics">
          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Active{filter.clinic ? ` · ${filter.clinic}` : ''}</div>
            <div className="text-2xl font-serif" style={{ color: C.ink }}>{metrics.active_total || 0}</div>
          </Card>
          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Offer acceptance (180d){filter.clinic ? ` · ${filter.clinic}` : ''}</div>
            <div className="text-2xl font-serif" style={{ color: C.ok }}>{metrics.offer_acceptance_rate_pct == null ? '—' : `${metrics.offer_acceptance_rate_pct}%`}</div>
            <div className="text-[10px]" style={{ color: C.soft }}>{metrics.accepted_last_180d} accepted · {metrics.declined_last_180d} declined</div>
          </Card>
          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Talent Pool{filter.clinic ? ` · ${filter.clinic}` : ''}</div>
            <div className="text-2xl font-serif" style={{ color: C.plum }}>{metrics.talent_pool_size || 0}</div>
          </Card>
          <Card className="p-3 border" style={{ borderColor: C.border }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Hired{filter.clinic ? ` · ${filter.clinic}` : ''}</div>
            <div className="text-2xl font-serif" style={{ color: C.ok }}>{metrics.by_stage?.Hired || 0}</div>
          </Card>
        </div>
      )}

      {filter.clinic && clinicStaff && (
        <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="hr-clinic-roster">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: C.plum }}>
                Currently staffed at {filter.clinic}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: C.soft }}>
                {clinicStaff.scorecard?.office_director
                  ? <>Office Director: <span className="font-semibold" style={{ color: C.ink2 }}>{clinicStaff.scorecard.office_director}</span></>
                  : <span style={{ color: C.rose }}>OD vacant</span>}
                {clinicStaff.scorecard && (
                  <> · {clinicStaff.scorecard.core_filled}/{clinicStaff.scorecard.core_target} core seats
                    {(clinicStaff.scorecard.supplemental_count || 0) > 0 && <> · {clinicStaff.scorecard.supplemental_count} supplemental</>}
                    {(clinicStaff.scorecard.incoming_count || 0) > 0 && <> · {clinicStaff.scorecard.incoming_count} incoming</>}
                    {(clinicStaff.scorecard.departing_count || 0) > 0 && <> · <span style={{ color: C.rose }}>{clinicStaff.scorecard.departing_count} departing</span></>}
                    {(clinicStaff.scorecard.open_positions || 0) > 0 && <> · <span style={{ color: C.rose }}>{clinicStaff.scorecard.open_positions} open</span></>}
                  </>
                )}
              </div>
            </div>
            <div className="text-[10px]" style={{ color: C.soft }}>{(clinicStaff.active_staff || []).length} active</div>
          </div>
          {(clinicStaff.active_staff || []).length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="hr-clinic-roster-chips">
              {(() => {
                const tierOf = (s) => {
                  if (s.is_od) return 0;
                  const role = (s.role || '').toLowerCase();
                  if (role.includes('extern') || role.includes('student')) return 2;
                  return 1;
                };
                const empShort = (t) => {
                  const v = (t || '').toLowerCase();
                  if (v.startsWith('full')) return 'FT';
                  if (v.startsWith('part')) return 'PT';
                  if (v.includes('prn')) return 'PRN';
                  if (v.includes('contract')) return 'Contract';
                  if (v.includes('extern')) return 'Extern';
                  if (v.includes('student')) return 'Student';
                  return t || '';
                };
                const sorted = [...clinicStaff.active_staff].sort((a, b) => {
                  const ta = tierOf(a), tb = tierOf(b);
                  if (ta !== tb) return ta - tb;
                  return (a.name || '').localeCompare(b.name || '');
                });
                return sorted.map(s => {
                  const emp = empShort(s.employment_type);
                  return (
                    <span key={s.clinician_id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border whitespace-nowrap"
                      style={{
                        borderColor: s.is_od ? C.plum : C.border,
                        background: s.on_leave ? '#F5EFDA' : (s.is_od ? '#F1EDF7' : '#FFFFFF'),
                        color: C.ink2,
                      }}
                      title={`${s.role || ''}${s.employment_type ? ' · ' + s.employment_type : ''}${s.on_leave ? ' · on leave' : ''}${s.termination_date ? ' · departing ' + s.termination_date : ''}`}
                    >
                      <span className="font-semibold" style={{ color: C.ink }}>
                        {s.name}{emp && <span style={{ color: C.soft }}> ({emp})</span>}
                      </span>
                      <span style={{ color: C.soft }}>· {s.credential || s.role || '—'}</span>
                      {s.is_od && <span className="uppercase text-[8px] font-bold" style={{ color: C.plum }}>OD</span>}
                      {s.on_leave && <span className="uppercase text-[8px] font-bold" style={{ color: C.gold || C.plum }}>Leave</span>}
                      {s.termination_date && <span className="uppercase text-[8px] font-bold" style={{ color: C.rose }}>Departing</span>}
                    </span>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="text-[11px] italic mt-2" style={{ color: C.soft }}>No active clinicians on this roster.</div>
          )}
        </Card>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: C.soft }}>Loading…</div>
      ) : view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="hr-kanban">
          {ACTIVE_STAGES.map(stage => (
            <div key={stage} className="rounded-lg" style={{ background: C.paper }}>
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center justify-between" style={{ color: C.ink2 }}>
                {stage}<span>{(byStage[stage] || []).length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto" data-testid={`hr-kanban-col-${stage.replace(/\s+/g, '-')}`}>
                {(byStage[stage] || []).map(c => <CandidateCard key={c.candidate_id} c={c} onOpen={setSelected} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-list-view">
          <table className="w-full text-sm">
            <thead style={{ background: C.paper }}>
              <tr className="text-[10px] uppercase tracking-wider" style={{ color: C.ink2 }}>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Stage</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">Clinics</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Applied</th>
                <th className="text-left px-4 py-2">Next action</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(c => (
                <tr key={c.candidate_id} className="border-t cursor-pointer hover:bg-[#FBF8F4]"
                    style={{ borderColor: C.border }} onClick={() => setSelected(c)}
                    data-testid={`hr-list-row-${c.candidate_id}`}>
                  <td className="px-4 py-2" style={{ color: C.ink }}>{c.name}</td>
                  <td className="px-4 py-2"><StageBadge stage={c.stage} /></td>
                  <td className="px-4 py-2" style={{ color: C.ink2 }}>{c.applied_role}</td>
                  <td className="px-4 py-2" style={{ color: C.ink2 }}>{(c.preferred_clinics || []).join(', ')}</td>
                  <td className="px-4 py-2" style={{ color: C.ink2 }}>{c.source}</td>
                  <td className="px-4 py-2" style={{ color: C.soft }}>{fmtDate(c.application_date)}</td>
                  <td className="px-4 py-2 text-[11px]" style={{ color: C.soft }}>
                    {c.next_action ? `${c.next_action} · ${c.next_action_due || '—'}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!candidates.length && <div className="p-6 text-center text-sm" style={{ color: C.soft }}>No candidates yet — click "New candidate" or import a CSV.</div>}
        </Card>
      )}

      <NewCandidateDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <CandidateDrawer candidate={selected} onClose={() => setSelected(null)} onChanged={load} />

      {/* Talent Pool ribbon — saved-for-future candidates surface here so HR
          can browse them when a position opens up. */}
      {(byStage['Talent Pool'] || []).length > 0 && (
        <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-talent-pool-ribbon">
          <div className="px-4 py-2.5 border-b flex items-center gap-2"
               style={{ borderColor: C.border, background: C.paper }}>
            <Bookmark size={14} style={{ color: C.gold }} />
            <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: C.ink2 }}>
              Talent Pool · {(byStage['Talent Pool'] || []).length} saved for future
            </div>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {(byStage['Talent Pool'] || []).map(c => (
              <CandidateCard key={c.candidate_id} c={c} onOpen={setSelected} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
